/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsStore } from '../../stores/analytics/analytics.store';
import { sessionEndEvent, sessionInfoEvent, sessionStartEvent } from '../../utils/analytics/session';
import { getViewerPlatform } from '../../utils/analytics/viewer';
import { getAuthToken } from '../../utils/auth';
import { addOsanoListeners, removeOsanoListeners } from '../../utils/osano';

export const useAnalyticsSession = () => {
  const authToken = getAuthToken();
  const { sessionExpiration, sessionId, userConsent, sessionInfoSent, setUserConsent, resetExpiration } =
    useAnalyticsStore();
  const authTokenRef = useRef(authToken);
  const sessionIdRef = useRef(sessionId);

  useEffect(() => {
    authTokenRef.current = authToken;
    sessionIdRef.current = sessionId;
  }, [authToken, sessionId]);

  const postSessionStartEvent = useQuery({
    queryKey: ['session.start'],
    queryFn: () => sessionStartEvent(),
    enabled: !!authToken,
  });

  const postSessionInfoEvent = useQuery({
    queryKey: ['session.info'],
    queryFn: () => sessionInfoEvent(),
    enabled: !!authToken && userConsent && !sessionInfoSent,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const handleSessionExpiration = useCallback(() => {
    const isSessionExpired = sessionExpiration ? Date.now() >= sessionExpiration : false;
    if (authToken && isSessionExpired && sessionId) {
      return sessionEndEvent();
    }
    return Promise.resolve(null);
  }, [authToken, sessionExpiration, sessionId]);

  const postSessionEndEvent = useQuery({
    queryKey: ['session.end'],
    queryFn: handleSessionExpiration,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    enabled: !!authToken,
  });

  // Page Close Listeners to handle the session_end event when the user close the window
  useEffect(() => {
    const platform = getViewerPlatform();
    sessionStorage.removeItem('isPageUnloading');

    const handlePageClose = () => {
      sessionStorage.removeItem('isPageUnloading');
      sessionStorage.removeItem('isPageClosing');
      if (authTokenRef.current && sessionIdRef.current) {
        void sessionEndEvent();
      }
    };

    const handleBeforeUnload = () => {
      sessionStorage.setItem('isPageUnloading', 'true');
      sessionStorage.setItem('isPageClosing', 'true');
      setTimeout(() => {
        sessionStorage.removeItem('isPageClosing');
      }, 200);
      return undefined;
    };

    const handlePageHide = () => {
      if (sessionStorage.getItem('isPageClosing') === 'true') {
        handlePageClose();
      }
    };

    const handlePageVisibilityChange = () => {
      if (
        platform === 'Mobile' &&
        sessionStorage.getItem('isPageUnloading') !== 'true' &&
        document.visibilityState === 'hidden'
      ) {
        handlePageClose();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handlePageVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handlePageVisibilityChange);
    };
  }, []);

  // Page Focus Listener to handle the session expiration reset
  useEffect(() => {
    const handlePageFocus = () => {
      if (authToken) {
        if (sessionId) {
          resetExpiration();
        } else {
          void sessionStartEvent();
        }
      }
    };

    window.addEventListener('focus', handlePageFocus);

    return () => {
      window.removeEventListener('focus', handlePageFocus);
    };
  }, [authToken]);

  // Osano listeners to handle the user Analytics consent changes
  useEffect(() => {
    addOsanoListeners({
      acceptCallback: () => {
        setUserConsent(true);
      },
      denyCallback: () => {
        setUserConsent(false);
      },
    });

    return () => {
      removeOsanoListeners();
    };
  }, []);

  return {
    postSessionStartEvent,
    postSessionEndEvent,
    postSessionInfoEvent,
  };
};
